/**
 * Mitarbeiter — Mitarbeiterliste mit Tabellen- und Card-Ansicht.
 *
 * Features:
 * - Professionelle Tabellenansicht (Default) mit Sortierung
 * - Card-Ansicht als Alternative
 * - Effektive Arbeitszeit, letzte Anwesenheit, Status-Badges
 * - Filter: Abteilung (kostenstelle), Standort, Status
 * - Suche nach Name, Kürzel, E-Mail, Job-Titel
 *
 * @page /mitarbeiter
 * @roles admin, manager
 * @api /api/mitarbeiter, /api/performance/effektive-arbeitszeit
 * @i18n employees
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useStandort } from '../hooks/useStandort';
import StandortSelect from '../components/StandortSelect';
import { PageHeader, Badge, Button } from '../components/ui';
import { AlertTriangle, UserPlus, X } from 'lucide-react';
import ConfirmDialog from '../components/ConfirmDialog';
import MitarbeiterAvatar from '../components/MitarbeiterAvatar';

function api(path, opts = {}) {
  return fetch(path, {
    headers: { 'Content-Type': 'application/json' },
    ...opts,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  }).then(r => r.json());
}

function fmtDate(d, locale) {
  if (!d) return '–';
  return new Date(d).toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function timeSince(dateStr, t) {
  if (!dateStr) return null;
  const start = new Date(dateStr);
  const now = new Date();
  let years = now.getFullYear() - start.getFullYear();
  let months = now.getMonth() - start.getMonth();
  if (months < 0) { years--; months += 12; }
  if (years > 0) return t('employees.time_years_months', { years, months });
  if (months > 0) return t('employees.time_months', { months });
  return t('employees.time_less_1m');
}

function daysSince(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.floor((now - d) / 86400000);
}

const KOSTENSTELLE_LABELS = {
  outbound: 'Outbound',
  inbound_retouren: 'Inbound/Retouren',
  verwaltung: 'Verwaltung',
};

// ═══ PAGE ═══
export default function MitarbeiterPage() {
  const { user } = useAuth();
  const { t, locale } = useLanguage();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [search, setSearch] = useState('');
  const [filterKostenstelle, setFilterKostenstelle] = useState('');
  const [filterStatus, setFilterStatus] = useState('active'); // 'active', 'all', 'inactive'
  const [view, setView] = useState('list');
  const { standorte, standortId, setStandortId } = useStandort();
  const [sortCol, setSortCol] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  const [effektivData, setEffektivData] = useState({});
  const [usersOhneMa, setUsersOhneMa] = useState([]);
  const [konsistenz, setKonsistenz] = useState(null);
  const [konsistenzExpanded, setKonsistenzExpanded] = useState(false);
  // #1864: laufende Phasen-Begrenzung (MA-ID) — verhindert Doppelklick.
  const [phasenFixBusy, setPhasenFixBusy] = useState(null);
  const [kvV2Stats, setKvV2Stats] = useState(null); // #1012 BR-23 banner
  const [quickUserModal, setQuickUserModal] = useState(null); // { maId, maName }
  const [quickUserEmail, setQuickUserEmail] = useState('');
  const [quickUserRolle, setQuickUserRolle] = useState('portal_mitarbeiter');
  const [quickUserSaving, setQuickUserSaving] = useState(false);
  const [quickUserResult, setQuickUserResult] = useState(null);
  const [linkDropdown, setLinkDropdown] = useState(null); // userId for linking
  const [linkConflict, setLinkConflict] = useState(null); // { userId, maId, message } for replace-confirmation
  const [linkError, setLinkError] = useState(null); // string error for info dialog

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (standortId) params.append('standort_id', standortId);
    if (filterStatus === 'ehemalig') params.append('status', 'ehemalig');
    else if (filterStatus === 'all') params.append('status', 'alle');
    if (filterKostenstelle) params.append('kostenstelle', filterKostenstelle);
    const url = '/api/mitarbeiter' + (params.toString() ? '?' + params : '');
    api(url).then(setData);
  }, [standortId, filterStatus, filterKostenstelle]);

  useEffect(() => { load(); }, [load]);

  // Load users without MA link
  useEffect(() => {
    api('/api/mitarbeiter/users-ohne-ma').then(setUsersOhneMa).catch(() => {});
  }, [data]);

  // #870: Load Arbeitszeit-Konsistenz-Check
  const ladeKonsistenz = useCallback(() => {
    api('/api/arbeitszeit/konsistenz-check').then(r => r.success ? setKonsistenz(r) : null).catch(() => {});
  }, []);
  useEffect(() => { ladeKonsistenz(); }, [data, ladeKonsistenz]);

  /**
   * #1864 — Arbeitszeitphase eines ausgetretenen MA auf sein Austrittsdatum
   * begrenzen. Der Knopf, der der Konsistenz-Meldung bisher fehlte. Danach den
   * Check neu laden, damit sichtbar ist, dass die Meldung wirklich weg ist —
   * ein stilles „gespeichert" wuerde genau die Unsicherheit lassen, die HR gemeldet hat.
   */
  const begrenzePhasenAufAustritt = useCallback(async (maId) => {
    setPhasenFixBusy(maId);
    try {
      const r = await api('/api/arbeitszeit/phasen/austritt-begrenzen', {
        method: 'POST',
        body: { mitarbeiter_id: maId },
      });
      if (!r?.success) {
        alert(r?.error || t('employees.konsistenz.phaseFixFehler', 'Phase konnte nicht begrenzt werden'));
      }
      ladeKonsistenz();
    } catch (e) {
      alert(e.message);
    } finally {
      setPhasenFixBusy(null);
    }
  }, [ladeKonsistenz, t]);

  // #1012 BR-23: KV-V2-Einstufung Banner-Daten
  useEffect(() => {
    api('/api/kv-einstufung/ohne-v2-count').then(r => {
      if (r && typeof r.ohne_v2 === 'number') setKvV2Stats(r);
    }).catch(() => {});
  }, [data]);

  const handleQuickUser = async () => {
    if (!quickUserModal || !quickUserEmail) return;
    setQuickUserSaving(true);
    try {
      const res = await fetch('/api/mitarbeiter/' + quickUserModal.maId + '/quick-user', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: quickUserEmail, rolle: quickUserRolle }),
      }).then(r => r.json());
      if (res.error) { setQuickUserResult({ error: res.error }); }
      else { setQuickUserResult({ ok: true, password: res.temp_password }); load(); }
    } catch (err) { setQuickUserResult({ error: err.message }); }
    finally { setQuickUserSaving(false); }
  };

  const handleLinkMa = async (userId, maId, force = false) => {
    try {
      const res = await fetch('/api/admin/users/' + userId + '/mapping' + (force ? '?force=true' : ''), {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mitarbeiter_id: maId }),
      });
      if (res.status === 409 && !force) {
        const data = await res.json().catch(() => ({}));
        const msg = data?.error || t('employees.linkConflictGeneric', 'MA bereits verknüpft.');
        setLinkConflict({ userId, maId, message: msg });
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setLinkError(data?.error || t('employees.linkErrorGeneric', 'Verknüpfung fehlgeschlagen.'));
        return;
      }
      setLinkDropdown(null);
      api('/api/mitarbeiter/users-ohne-ma').then(setUsersOhneMa).catch(() => {});
      load();
    } catch { /* ignore */ }
  };

  // Load effektive Arbeitszeit for current month
  useEffect(() => {
    const now = new Date();
    const von = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    const bis = now.toISOString().slice(0, 10);
    fetch(`/api/performance/effektive-arbeitszeit?von=${von}&bis=${bis}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d?.mitarbeiter) return;
        const map = {};
        d.mitarbeiter.forEach(m => { map[m.mitarbeiter_id] = m.effektiv_pct; });
        setEffektivData(map);
      }).catch(() => {});
  }, []);

  const kostenstellen = useMemo(() => {
    const set = new Set(data.map(m => m.kostenstelle).filter(Boolean));
    return [...set].sort();
  }, [data]);

  const abteilungen = useMemo(() => {
    const set = new Set(data.map(m => m.abteilung_name).filter(Boolean));
    return [...set].sort();
  }, [data]);

  const [filterAbteilung, setFilterAbteilung] = useState('');

  const filtered = useMemo(() => {
    let list = data;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(m =>
        m.name?.toLowerCase().includes(q) ||
        m.work_email?.toLowerCase().includes(q) ||
        m.berufsbezeichnung?.toLowerCase().includes(q) ||
        m.kuerzel?.toLowerCase().includes(q)
      );
    }
    if (filterAbteilung) {
      list = list.filter(m => m.abteilung_name === filterAbteilung);
    }
    return list;
  }, [data, search, filterStatus, filterAbteilung]);

  // Sorting
  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      let va, vb;
      switch (sortCol) {
        case 'name': va = a.name || ''; vb = b.name || ''; break;
        case 'kostenstelle': va = a.kostenstelle || ''; vb = b.kostenstelle || ''; break;
        case 'stunden': va = parseFloat(a.wochenstunden_soll ?? a.stunden_pro_woche) || 0; vb = parseFloat(b.wochenstunden_soll ?? b.stunden_pro_woche) || 0; break;
        case 'eintritt': va = a.dabei_seit || ''; vb = b.dabei_seit || ''; break;
        case 'effektiv': va = effektivData[a.id] ?? -1; vb = effektivData[b.id] ?? -1; break;
        case 'anwesenheit': va = a.letzte_anwesenheit || ''; vb = b.letzte_anwesenheit || ''; break;
        case 'zugang': va = a.user_id ? 1 : 0; vb = b.user_id ? 1 : 0; break;
        case 'status': va = a.aktiv ? 1 : 0; vb = b.aktiv ? 1 : 0; break;
        default: va = a.name || ''; vb = b.name || '';
      }
      if (typeof va === 'string') {
        const cmp = va.localeCompare(vb, 'de');
        return sortDir === 'asc' ? cmp : -cmp;
      }
      return sortDir === 'asc' ? va - vb : vb - va;
    });
    return arr;
  }, [filtered, sortCol, sortDir, effektivData]);

  const toggleSort = (col) => {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <span className="text-[var(--text-muted)] ml-0.5">↕</span>;
    return <span className="text-[#338edc] ml-0.5">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const activeCount = data.filter(m => m.aktiv).length;

  return (
    <div className="p-page">
      {/* Header */}
      <PageHeader
        title={t('employees.title')}
        subtitle={t('employees.active_count', { count: activeCount })}
        className="mb-section"
      />

      {/* #1012 BR-23: KV-V2-Einstufung Hinweis (informativ, blau) */}
      {kvV2Stats && kvV2Stats.ohne_v2 > 0 && (
        <div className="mb-4 border border-blue-300 bg-blue-50 dark:bg-blue-500/10 dark:border-blue-500/40 rounded-xl p-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-blue-800 dark:text-blue-300">
                {t('employees.kvV2.bannerTitle', 'KV-Einstufung (neu) erfassen')}
              </p>
              <p className="text-xs text-blue-700 dark:text-blue-200 mt-1">
                {t('employees.kvV2.bannerHint', '{{count}} von {{total}} MA haben noch keine V2-Einstufung. HR bitte manuell setzen (Stammdaten → Anstellung).',
                  { count: kvV2Stats.ohne_v2, total: kvV2Stats.gesamt })}
              </p>
              {Array.isArray(kvV2Stats.liste) && kvV2Stats.liste.length > 0 && (
                <div className="mt-3 space-y-0.5 text-xs">
                  {kvV2Stats.liste.slice(0, 20).map((m) => (
                    <button key={m.id} onClick={() => navigate(`/mitarbeiter/${m.id}`)}
                      className="block text-left text-blue-900 dark:text-blue-200 hover:underline">
                      {m.name}
                    </button>
                  ))}
                  {kvV2Stats.liste.length > 20 && (
                    <p className="text-blue-700 dark:text-blue-400">… +{kvV2Stats.liste.length - 20} {t('employees.konsistenz.more', 'weitere')}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* #885: Aktive MAs ohne Vertrag — separate ROTE Card (höchste Priorität) */}
      {konsistenz && konsistenz.aktiv_ohne_vertrag?.length > 0 && (
        <div className="mb-4 border border-red-300 bg-red-50 dark:bg-red-500/10 dark:border-red-500/40 rounded-xl p-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-800 dark:text-red-400">
                {t('employees.konsistenz.aktivOhneVertragHeader', 'Aktive Mitarbeiter ohne gültigen Vertrag')} ({konsistenz.aktiv_ohne_vertrag.length})
              </p>
              <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                {t('employees.konsistenz.aktivOhneVertragHint', 'Stempel und Abwesenheiten werden verarbeitet, aber HR sollte Vertrag anlegen oder Austrittsdatum setzen.')}
              </p>
              <div className="mt-3 space-y-0.5 text-xs">
                {konsistenz.aktiv_ohne_vertrag.slice(0, 20).map((m, i) => (
                  <button key={i} onClick={() => navigate(`/mitarbeiter/${m.mitarbeiter_id}`)}
                    className="block text-left text-red-900 dark:text-red-200 hover:underline">
                    {m.vorname} {m.nachname} — {m.letzter_vertrag_end
                      ? <>{t('employees.konsistenz.lastContractUntil', 'letzter Vertrag bis')} {m.letzter_vertrag_end} ({m.tage_ohne_vertrag} {t('employees.konsistenz.daysShort', 'T')})</>
                      : t('employees.konsistenz.noContractEver', 'nie Vertrag angelegt')}
                  </button>
                ))}
                {konsistenz.aktiv_ohne_vertrag.length > 20 && (
                  <p className="text-red-700 dark:text-red-400">… +{konsistenz.aktiv_ohne_vertrag.length - 20} weitere</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* #870: Arbeitszeit-Konsistenz-Card (Eintritt/Phasen/Lücken) */}
      {konsistenz && konsistenz.summary.gesamt_probleme > 0 && (
        <div className="mb-4 border border-amber-300 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/30 rounded-xl p-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <button
                onClick={() => setKonsistenzExpanded(e => !e)}
                className="w-full text-left"
              >
                <p className="text-sm font-semibold text-amber-800 dark:text-amber-400">
                  {t('employees.konsistenz.title', 'Arbeitszeit-Konsistenz')} — {konsistenz.summary.gesamt_probleme} {t('employees.konsistenz.problems', 'Problem(e)')}
                  <span className="ml-2 text-xs font-normal text-amber-700 dark:text-amber-300">
                    ({konsistenz.summary.ohne_eintritt} {t('employees.konsistenz.ohneEintritt', 'ohne Eintritt')},{' '}
                    {konsistenz.summary.luecken} {t('employees.konsistenz.luecken', 'Lücken')},{' '}
                    {konsistenz.summary.phasen_ausserhalb} {t('employees.konsistenz.phasenAusserhalb', 'Phasen außerhalb')},{' '}
                    {konsistenz.summary.ueberlappungen} {t('employees.konsistenz.ueberlappungen', 'Überlappungen')}
                    {konsistenz.summary.phasen_ohne_vertrag > 0 && <>, {konsistenz.summary.phasen_ohne_vertrag} {t('employees.konsistenz.phasenOhneVertrag', 'ohne Vertrag')}</>}
                    {konsistenz.summary.ohne_lohnkosten > 0 && <>, {konsistenz.summary.ohne_lohnkosten} {t('employees.konsistenz.ohneLohnkosten', 'ohne Lohnkosten')}</>})
                  </span>
                  <span className="ml-2 text-xs text-amber-700 dark:text-amber-400">{konsistenzExpanded ? '▲' : '▼'}</span>
                </p>
              </button>
              {konsistenzExpanded && (
                <div className="mt-3 space-y-3 text-xs">
                  {konsistenz.ohne_eintritt.length > 0 && (
                    <div>
                      <p className="font-semibold text-amber-800 dark:text-amber-300 mb-1">
                        {t('employees.konsistenz.ohneEintrittHeader', 'MAs ohne Eintritt')} ({konsistenz.ohne_eintritt.length})
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {konsistenz.ohne_eintritt.map(m => (
                          <button key={m.id} onClick={() => navigate(`/mitarbeiter/${m.id}`)}
                            className="px-2 py-0.5 rounded bg-amber-200/60 dark:bg-amber-500/20 text-amber-900 dark:text-amber-200 hover:bg-amber-200">
                            {m.vorname} {m.nachname}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {konsistenz.luecken.length > 0 && (
                    <div>
                      <p className="font-semibold text-amber-800 dark:text-amber-300 mb-1">
                        {t('employees.konsistenz.lueckenHeader', 'Phasen-Lücken')} ({konsistenz.luecken.length})
                      </p>
                      <div className="space-y-0.5">
                        {konsistenz.luecken.map((l, i) => (
                          <button key={i} onClick={() => navigate(`/mitarbeiter/${l.mitarbeiter_id}`)}
                            className="block text-left text-amber-900 dark:text-amber-200 hover:underline">
                            {l.vorname} {l.nachname}: {l.prev_bis} → {l.naechste_ab} ({l.luecke_tage} {t('employees.konsistenz.dayGap', 'Tag(e)')})
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {konsistenz.phasen_ausserhalb.length > 0 && (
                    <div>
                      <p className="font-semibold text-amber-800 dark:text-amber-300 mb-1">
                        {t('employees.konsistenz.phasenAusserhalbHeader', 'Phasen außerhalb Eintritt/Austritt')} ({konsistenz.phasen_ausserhalb.length})
                      </p>
                      {/* #1864: Die Meldung nannte bisher nur den Zustand. Was zu tun ist,
                          war von der Oberflaeche aus nicht erkennbar — HR musste raten. */}
                      <p className="text-amber-700 dark:text-amber-400 mb-1.5">
                        {t('employees.konsistenz.phasenAusserhalbHint', 'Die Arbeitszeitphase reicht über das Austrittsdatum hinaus oder hat kein Ende. Schließen begrenzt sie auf das Austrittsdatum — Austritt und bisherige Zeitdaten bleiben unverändert.')}
                      </p>
                      <div className="space-y-0.5">
                        {konsistenz.phasen_ausserhalb.slice(0, 20).map((p, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <button onClick={() => navigate(`/mitarbeiter/${p.mitarbeiter_id}`)}
                              className="text-left text-amber-900 dark:text-amber-200 hover:underline">
                              {p.vorname} {p.nachname}: {p.phase_ab}{p.phase_bis ? ` – ${p.phase_bis}` : ` – ${t('employees.konsistenz.phaseOhneEnde', 'ohne Ende')}`} ({p.issue})
                            </button>
                            {p.austritt && (p.issue === 'offen_trotz_austritt' || p.issue === 'nach_austritt') && (
                              <button
                                disabled={phasenFixBusy === p.mitarbeiter_id}
                                onClick={() => begrenzePhasenAufAustritt(p.mitarbeiter_id)}
                                className="px-2 py-0.5 rounded bg-amber-200/60 dark:bg-amber-500/20 text-amber-900 dark:text-amber-200 hover:bg-amber-200 disabled:opacity-50 whitespace-nowrap">
                                {phasenFixBusy === p.mitarbeiter_id
                                  ? t('common.saving', 'Speichert…')
                                  : t('employees.konsistenz.phaseAufAustrittSchliessen', 'Auf {{datum}} schließen', { datum: p.austritt })}
                              </button>
                            )}
                          </div>
                        ))}
                        {konsistenz.phasen_ausserhalb.length > 20 && (
                          <p className="text-amber-700 dark:text-amber-400">… +{konsistenz.phasen_ausserhalb.length - 20} weitere</p>
                        )}
                      </div>
                    </div>
                  )}
                  {konsistenz.ueberlappungen.length > 0 && (
                    <div>
                      <p className="font-semibold text-amber-800 dark:text-amber-300 mb-1">
                        {t('employees.konsistenz.ueberlappungenHeader', 'Phasen-Überlappungen')} ({konsistenz.ueberlappungen.length})
                      </p>
                      <div className="space-y-0.5">
                        {konsistenz.ueberlappungen.map((u, i) => (
                          <button key={i} onClick={() => navigate(`/mitarbeiter/${u.mitarbeiter_id}`)}
                            className="block text-left text-amber-900 dark:text-amber-200 hover:underline">
                            {u.vorname} {u.nachname}: {u.prev_ab} – {u.prev_bis} ⟷ {u.naechste_ab}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {konsistenz.phasen_ohne_vertrag?.length > 0 && (
                    <div>
                      <p className="font-semibold text-amber-800 dark:text-amber-300 mb-1">
                        {t('employees.konsistenz.phasenOhneVertragHeader', 'Arbeitszeit-Phasen ohne Dienstvertrag')} ({konsistenz.phasen_ohne_vertrag.length})
                      </p>
                      <div className="space-y-0.5">
                        {konsistenz.phasen_ohne_vertrag.slice(0, 20).map((p, i) => (
                          <button key={i} onClick={() => navigate(`/mitarbeiter/${p.mitarbeiter_id}`)}
                            className="block text-left text-amber-900 dark:text-amber-200 hover:underline">
                            {p.vorname} {p.nachname}: {p.phase_ab} – {p.phase_bis || t('employees.konsistenz.open', 'offen')} ({p.ungedeckte_tage} {t('employees.konsistenz.daysUncovered', 'Tage ungedeckt')})
                          </button>
                        ))}
                        {konsistenz.phasen_ohne_vertrag.length > 20 && (
                          <p className="text-amber-700 dark:text-amber-400">… +{konsistenz.phasen_ohne_vertrag.length - 20} weitere</p>
                        )}
                      </div>
                    </div>
                  )}
                  {konsistenz.ohne_lohnkosten?.length > 0 && (
                    <div>
                      <p className="font-semibold text-amber-800 dark:text-amber-300 mb-1">
                        {t('employees.konsistenz.ohneLohnkostenHeader', 'MAs ohne Jahres-Lohnkosten')} ({konsistenz.ohne_lohnkosten.length})
                      </p>
                      <div className="space-y-0.5">
                        {konsistenz.ohne_lohnkosten.slice(0, 20).map((m, i) => (
                          <button key={i} onClick={() => navigate(`/mitarbeiter/${m.mitarbeiter_id}`)}
                            className="block text-left text-amber-900 dark:text-amber-200 hover:underline">
                            {m.vorname} {m.nachname}
                          </button>
                        ))}
                        {konsistenz.ohne_lohnkosten.length > 20 && (
                          <p className="text-amber-700 dark:text-amber-400">… +{konsistenz.ohne_lohnkosten.length - 20} weitere</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Warning: Users without MA link */}
      {usersOhneMa.length > 0 && (
        <div className="mb-4 border border-amber-300 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/30 rounded-xl p-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-800 dark:text-amber-400">
                {t('employees.usersWithoutMa', { count: usersOhneMa.length })}
              </p>
              <div className="mt-2 space-y-1.5">
                {usersOhneMa.map(u => (
                  <div key={u.id} className="flex items-center gap-2 text-xs text-amber-700 dark:text-amber-300">
                    <span className="font-medium">{u.name || u.email}</span>
                    <span className="text-amber-600/60">({u.rolle})</span>
                    <div className="relative ml-auto">
                      <button
                        onClick={() => setLinkDropdown(linkDropdown === u.id ? null : u.id)}
                        className="px-2 py-0.5 rounded bg-amber-200/60 dark:bg-amber-500/20 text-amber-800 dark:text-amber-300 hover:bg-amber-200 text-[10px] font-semibold"
                      >
                        {t('employees.linkMa')}
                      </button>
                      {linkDropdown === u.id && (
                        <div className="absolute right-0 top-6 z-20 bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-lg shadow-sm max-h-72 overflow-y-auto w-64">
                          {data.filter(m => m.aktiv).sort((a,b)=> (a.user_id?1:0)-(b.user_id?1:0)).map(m => (
                            <button key={m.id} onClick={() => handleLinkMa(u.id, m.id)}
                              className="w-full text-left px-3 py-1.5 text-xs text-[var(--text-primary)] hover:bg-[var(--bg-inset,#F8F9FC)] flex items-center justify-between gap-2">
                              <span className="truncate">{m.name}</span>
                              {m.user_id && (
                                <span className="flex-shrink-0 px-1.5 py-0.5 text-[9px] font-semibold rounded-full bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400" title={t('employees.alreadyLinkedTo', { email: m.user_email || '' })}>
                                  {t('employees.alreadyLinked', 'verknüpft')}
                                </span>
                              )}
                            </button>
                          ))}
                          {data.filter(m => m.aktiv).length === 0 && (
                            <div className="px-3 py-2 text-xs text-[var(--text-tertiary)]">{t('employees.noMaForLink', 'Keine MA verfügbar')}</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <button onClick={() => navigate('/mitarbeiter/onboarding')}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white"
          style={{ background: '#338edc' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>
          {t('mitarbeiter.neuAnlegen', 'Neuer Mitarbeiter')}
        </button>
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder={t('employees.search_placeholder')}
            className="w-full bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-lg pl-9 pr-3 py-2 text-sm text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none focus:border-[#338edc] focus:ring-1 focus:ring-[#338edc]/20" />
        </div>

        <select value={filterKostenstelle} onChange={e => setFilterKostenstelle(e.target.value)}
          className="px-3 py-2 bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm text-[var(--text-primary)] focus:outline-none appearance-none cursor-pointer">
          <option value="">{t('employees.all_departments')}</option>
          {kostenstellen.map(k => <option key={k} value={k}>{KOSTENSTELLE_LABELS[k] || k}</option>)}
        </select>

        <select value={filterAbteilung} onChange={e => setFilterAbteilung(e.target.value)}
          className="px-3 py-2 bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm text-[var(--text-primary)] focus:outline-none appearance-none cursor-pointer"
          title={t('employees.filterAbteilung', 'Abteilung filtern')}>
          <option value="">{t('employees.allAbteilungen', 'Alle Abteilungen')}</option>
          {abteilungen.map(a => <option key={a} value={a}>{a}</option>)}
        </select>

        <StandortSelect standorte={standorte} standortId={standortId} setStandortId={setStandortId} alleOption className="py-2 text-sm" />

        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="px-3 py-2 bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm text-[var(--text-primary)] focus:outline-none appearance-none cursor-pointer">
          <option value="active">{t('employees.status_active')}</option>
          <option value="ehemalig">{t('employees.status_ehemalig')}</option>
          <option value="all">{t('employees.status_all')}</option>
        </select>

        <div className="flex bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-default,#E5E7EB)]">
          <button onClick={() => setView('list')} className={`px-2.5 py-1 rounded-[8px] text-xs font-medium transition-all ${view === 'list' ? 'bg-white shadow-sm text-[var(--text-primary)] dark:bg-[var(--bg-card)]' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"/></svg>
          </button>
          <button onClick={() => setView('card')} className={`px-2.5 py-1 rounded-[8px] text-xs font-medium transition-all ${view === 'card' ? 'bg-white shadow-sm text-[var(--text-primary)] dark:bg-[var(--bg-card)]' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
          </button>
        </div>
      </div>

      {/* List View (Default) */}
      {view === 'list' && (
        <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[var(--bg-inset,#F8F9FC)]">
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] w-10"></th>
                  <th onClick={() => toggleSort('personalnummer')} className="px-4 py-3 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] cursor-pointer select-none hover:text-[var(--text-primary)] w-24">
                    {t('employees.col_nr', 'Nr.')} <SortIcon col="personalnummer" />
                  </th>
                  <th onClick={() => toggleSort('name')} className="px-4 py-3 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] cursor-pointer select-none hover:text-[var(--text-primary)]">
                    {t('employees.col_name')} <SortIcon col="name" />
                  </th>
                  <th onClick={() => toggleSort('kostenstelle')} className="px-4 py-3 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] cursor-pointer select-none hover:text-[var(--text-primary)]">
                    {t('employees.col_department')} <SortIcon col="kostenstelle" />
                  </th>
                  <th onClick={() => toggleSort('stunden')} className="px-4 py-3 text-right text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] cursor-pointer select-none hover:text-[var(--text-primary)]">
                    {t('employees.col_h_week')} <SortIcon col="stunden" />
                  </th>
                  <th onClick={() => toggleSort('eintritt')} className="px-4 py-3 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] cursor-pointer select-none hover:text-[var(--text-primary)]">
                    {t('employees.col_since')} <SortIcon col="eintritt" />
                  </th>
                  <th onClick={() => toggleSort('effektiv')} className="px-4 py-3 text-right text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] cursor-pointer select-none hover:text-[var(--text-primary)]">
                    {t('employees.col_effective')} <SortIcon col="effektiv" />
                  </th>
                  <th onClick={() => toggleSort('anwesenheit')} className="px-4 py-3 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] cursor-pointer select-none hover:text-[var(--text-primary)]">
                    {t('employees.col_last_attendance')} <SortIcon col="anwesenheit" />
                  </th>
                  <th onClick={() => toggleSort('zugang')} className="px-4 py-3 text-center text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] cursor-pointer select-none hover:text-[var(--text-primary)]">
                    {t('employees.col_access')} <SortIcon col="zugang" />
                  </th>
                  <th onClick={() => toggleSort('status')} className="px-4 py-3 text-center text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] cursor-pointer select-none hover:text-[var(--text-primary)]">
                    {t('employees.col_status')} <SortIcon col="status" />
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-default,#E5E7EB)]">
                {sorted.map(ma => {
                  const isFormer = !ma.aktiv;
                  const daysAgo = daysSince(ma.letzte_anwesenheit);
                  return (
                  <tr key={ma.id} onClick={() => navigate(`/mitarbeiter/${ma.id}`)}
                    className={`hover:bg-[var(--bg-inset,#F8F9FC)] cursor-pointer transition-colors ${isFormer ? 'opacity-50' : ''}`}>
                    <td className="px-4 py-2.5">
                      <MitarbeiterAvatar
                        imageUrl={(ma.foto_pfad || ma.image_url) ? `/api/mitarbeiter/${ma.id}/foto` : null}
                        name={ma.name} size={36} />
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs text-[var(--text-secondary)]">
                      {ma.personalnummer || '–'}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-[var(--text-primary)]">{ma.name}</div>
                      {(() => {
                        const title = ma.berufsbezeichnung;
                        const parts = [title, ma.abteilung_name].filter(Boolean);
                        return parts.length > 0 ? (
                          <div className="text-xs text-[var(--text-tertiary)]">{parts.join(' — ')}</div>
                        ) : null;
                      })()}
                    </td>
                    <td className="px-4 py-2.5">
                      {(ma.abteilung_name || ma.kostenstelle) ? (
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400">
                          {ma.abteilung_name || KOSTENSTELLE_LABELS[ma.kostenstelle] || ma.kostenstelle}
                        </span>
                      ) : <span className="text-[var(--text-muted)]">–</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-[var(--text-primary)]">
                      {(() => { const w = ma.wochenstunden_soll ?? ma.stunden_pro_woche; return w ? `${w}h` : '–'; })()}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-[var(--text-secondary)]">
                      {ma.dabei_seit ? (
                        <span title={fmtDate(ma.dabei_seit, locale)}>
                          {fmtDate(ma.dabei_seit, locale)}
                          <span className="text-[var(--text-tertiary)] ml-1">({timeSince(ma.dabei_seit, t)})</span>
                        </span>
                      ) : '–'}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-xs">
                      {effektivData[ma.id] != null ? (
                        <span className="text-[var(--text-primary)] font-bold">{effektivData[ma.id].toFixed(0)}%</span>
                      ) : <span className="text-[var(--text-muted)]">–</span>}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-[var(--text-secondary)]">
                      {ma.letzte_anwesenheit ? (
                        <span className={daysAgo === 0 ? 'text-green-600 dark:text-emerald-400 font-medium' : daysAgo <= 2 ? 'text-[var(--text-secondary)]' : 'text-[var(--text-tertiary)]'}>
                          {daysAgo === 0 ? t('employees.today') : daysAgo === 1 ? t('employees.yesterday') : fmtDate(ma.letzte_anwesenheit, locale)}
                        </span>
                      ) : <span className="text-[var(--text-muted)]">–</span>}
                    </td>
                    <td className="px-4 py-2.5 text-center" onClick={e => e.stopPropagation()}>
                      {ma.user_id ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                          {['admin', 'administrator', 'ops_manager'].includes(ma.user_rolle) ? 'Admin' : 'Portal'}
                        </span>
                      ) : (
                        <button onClick={() => { setQuickUserModal({ maId: ma.id, maName: ma.name }); setQuickUserEmail(ma.work_email || ''); setQuickUserRolle('portal_mitarbeiter'); setQuickUserResult(null); }}
                          className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-400 hover:bg-red-200 transition-colors">
                          <UserPlus className="w-3 h-3" /> {t('employees.createAccount')}
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      {ma.aktiv ? (
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700 dark:bg-emerald-500/15 dark:text-emerald-400">{t('employees.active')}</span>
                      ) : ma.austritt_datum ? (
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400">{t('employees.status_ehemalig')}</span>
                      ) : (
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-500 dark:bg-gray-500/15 dark:text-gray-400">{t('employees.inactive')}</span>
                      )}
                    </td>
                  </tr>
                  );
                })}
                {sorted.length === 0 && (
                  <tr><td colSpan={9} className="px-4 py-12 text-center text-[var(--text-tertiary)]">{t('employees.no_employees_found')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {/* Footer count */}
          <div className="px-4 py-2.5 border-t border-[var(--border-default,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)] text-xs text-[var(--text-tertiary)]">
            {t('employees.showing_count', { shown: sorted.length, total: data.length })}
          </div>
        </div>
      )}

      {/* Card View */}
      {view === 'card' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {sorted.map(ma => {
            const isFormer = !ma.aktiv;
            return (
            <div key={ma.id} onClick={() => navigate(`/mitarbeiter/${ma.id}`)}
              className={`border border-[var(--border-default,#E5E7EB)] rounded-xl p-4 hover:border-[#338edc] cursor-pointer transition-all group ${isFormer ? 'opacity-50' : ''}`}>
              <div className="flex items-start gap-3 mb-3">
                <MitarbeiterAvatar
                  imageUrl={(ma.foto_pfad || ma.image_url) ? `/api/mitarbeiter/${ma.id}/foto` : null}
                  name={ma.name} size={48} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[var(--text-primary)] text-sm truncate group-hover:text-[#338edc] transition-colors">
                    {ma.name}
                  </div>
                  {(() => {
                    const title = ma.berufsbezeichnung;
                    const parts = [title, ma.abteilung_name].filter(Boolean);
                    return parts.length > 0 ? (
                      <div className="text-[11px] text-[var(--text-tertiary)] truncate">{parts.join(' — ')}</div>
                    ) : null;
                  })()}
                  <div className="flex items-center gap-1.5 mt-1">
                    {(ma.abteilung_name || ma.kostenstelle) && (
                      <span className="px-1.5 py-0.5 text-[9px] font-medium rounded-full bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400">
                        {ma.abteilung_name || KOSTENSTELLE_LABELS[ma.kostenstelle] || ma.kostenstelle}
                      </span>
                    )}
                    {!ma.aktiv && (
                      <span className={`px-1.5 py-0.5 text-[9px] font-medium rounded-full ${ma.austritt_datum ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>
                        {ma.austritt_datum ? t('employees.status_ehemalig') : t('employees.inactive')}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 text-[10px]">
                <div>
                  <div className="text-[var(--text-tertiary)] uppercase">{t('employees.h_per_week')}</div>
                  <div className="font-mono font-bold text-[var(--text-primary)] text-xs">{(() => { const w = ma.wochenstunden_soll ?? ma.stunden_pro_woche; return w ? `${w}h` : '–'; })()}</div>
                </div>
                <div>
                  <div className="text-[var(--text-tertiary)] uppercase">{t('employees.effective')}</div>
                  <div className="font-mono font-bold text-xs">
                    {effektivData[ma.id] != null
                      ? <span className="text-[var(--text-primary)]">{effektivData[ma.id].toFixed(0)}%</span>
                      : <span className="text-[var(--text-muted)]">–</span>}
                  </div>
                </div>
                <div>
                  <div className="text-[var(--text-tertiary)] uppercase">{t('employees.since')}</div>
                  <div className="text-[var(--text-primary)] text-xs truncate">
                    {ma.dabei_seit ? timeSince(ma.dabei_seit, t) : '–'}
                  </div>
                </div>
              </div>

              {ma.work_email && (
                <div className="mt-2 pt-2 border-t border-[var(--border-default,#E5E7EB)]/50 text-[10px] text-[var(--text-tertiary)] truncate">{ma.work_email}</div>
              )}
            </div>
          );
          })}
          {sorted.length === 0 && (
            <div className="col-span-full py-12 text-center text-[var(--text-tertiary)] text-sm">
              {t('employees.no_employees_found')}
            </div>
          )}
        </div>
      )}

      {/* Quick-User Modal */}
      {quickUserModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => setQuickUserModal(null)} />
          <div className="relative bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-xl p-6 w-full max-w-md shadow-sm">
            <button onClick={() => setQuickUserModal(null)} className="absolute top-3 right-3 text-[var(--text-tertiary)] hover:text-[var(--text-primary)]">
              <X className="w-4 h-4" />
            </button>
            <h3 className="text-sm font-bold text-[var(--text-primary)] mb-1">{t('employees.createAccountFor')}</h3>
            <p className="text-xs text-[var(--text-secondary)] mb-4">{quickUserModal.maName}</p>

            {quickUserResult?.ok ? (
              <div className="bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 rounded-lg p-4 text-sm">
                <p className="font-semibold text-emerald-700 dark:text-emerald-400">{t('employees.accountCreated')}</p>
                <p className="text-xs text-emerald-600 dark:text-emerald-300 mt-1">
                  {t('employees.tempPassword')}: <code className="font-mono bg-emerald-100 dark:bg-emerald-500/20 px-1 rounded">{quickUserResult.password}</code>
                </p>
                <button onClick={() => setQuickUserModal(null)} className="mt-3 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700">
                  {t('common.close')}
                </button>
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-[var(--text-secondary)] mb-1 block">{t('employees.email')}</label>
                    <input type="email" value={quickUserEmail} onChange={e => setQuickUserEmail(e.target.value)}
                      className="w-full px-3 py-2 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[var(--text-secondary)] mb-1 block">{t('employees.role')}</label>
                    <select value={quickUserRolle} onChange={e => setQuickUserRolle(e.target.value)}
                      className="w-full px-3 py-2 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary)] focus:outline-none">
                      <option value="portal_mitarbeiter">Portal (MA)</option>
                      <option value="viewer">Viewer</option>
                      <option value="ops_manager">Manager</option>
                      <option value="administrator">Administrator</option>
                    </select>
                  </div>
                </div>
                {quickUserResult?.error && (
                  <p className="text-xs text-red-600 mt-2">{quickUserResult.error}</p>
                )}
                <div className="flex justify-end gap-2 mt-4">
                  <button onClick={() => setQuickUserModal(null)}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium border border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)]">
                    {t('common.cancel')}
                  </button>
                  <button onClick={handleQuickUser} disabled={quickUserSaving || !quickUserEmail}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#338edc] text-white hover:bg-[#2b7bc4] disabled:opacity-50">
                    {quickUserSaving ? '...' : t('employees.createBtn')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!linkConflict}
        title={t('employees.linkConflictTitle', 'Verknüpfung-Konflikt')}
        message={linkConflict ? `${linkConflict.message}\n\n${t('employees.linkConflictReplace', 'Mit diesem User trotzdem verknüpfen? (alte Verknüpfung wird gelöst)')}` : ''}
        confirmLabel={t('employees.linkConflictReplaceBtn', 'Trotzdem verknüpfen')}
        variant="danger"
        onConfirm={() => {
          const c = linkConflict;
          setLinkConflict(null);
          if (c) handleLinkMa(c.userId, c.maId, true);
        }}
        onCancel={() => setLinkConflict(null)}
      />

      <ConfirmDialog
        open={!!linkError}
        title={t('employees.linkErrorTitle', 'Fehler')}
        message={linkError || ''}
        infoOnly
        onConfirm={() => setLinkError(null)}
        onCancel={() => setLinkError(null)}
      />
    </div>
  );
}
